import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Toast as Primitive } from 'radix-ui';
import { cn } from '../lib/cn';

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: 'default' | 'error';
  /** Rotulo e acao do botao. Secao 2.4: toda acao destrutiva oferece desfazer. */
  action?: { label: string; onAction: () => void };
  /** Padrao 8000 ms - a janela de undo do produto. */
  duration?: number;
}

type ToastRecord = ToastOptions & { id: number };

const ToastContext = createContext<((options: ToastOptions) => void) | null>(null);

export function useToast(): (options: ToastOptions) => void {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast precisa estar dentro de <ToastProvider>.');
  return ctx;
}

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const push = useCallback((options: ToastOptions) => {
    const id = nextId++;
    setToasts((current) => [...current, { ...options, id }]);
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      <Primitive.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <Primitive.Root
            key={toast.id}
            duration={toast.duration ?? 8000}
            onOpenChange={(open) => {
              if (!open) remove(toast.id);
            }}
            className={cn(
              'flex items-center gap-3 rounded-wc-lg border bg-raised px-4 py-3 shadow-wc-lg',
              // Sem acao nao ha o que clicar: nao captura o ponteiro, para o aviso
              // nao engolir o clique em quem esta por baixo (B2).
              toast.action === undefined ? 'pointer-events-none' : 'pointer-events-auto',
              toast.tone === 'error' ? 'border-tint-error-border' : 'border-subtle',
            )}
          >
            <div className="min-w-0 flex-1">
              <Primitive.Title className="text-[14px] font-semibold text-primary">
                {toast.title}
              </Primitive.Title>
              {toast.description !== undefined && (
                <Primitive.Description className="mt-0.5 text-[13px] text-secondary">
                  {toast.description}
                </Primitive.Description>
              )}
            </div>
            {toast.action !== undefined && (
              <Primitive.Action
                altText={toast.action.label}
                onClick={toast.action.onAction}
                className="shrink-0 cursor-pointer rounded-wc-md px-3 py-1.5 text-[13px] font-semibold text-accent transition-colors hover:bg-selected"
              >
                {toast.action.label}
              </Primitive.Action>
            )}
          </Primitive.Root>
        ))}
        <Primitive.Viewport className="pointer-events-none fixed right-4 bottom-4 z-100 flex w-[380px] max-w-[calc(100vw-32px)] flex-col gap-2 outline-none" />
      </Primitive.Provider>
    </ToastContext.Provider>
  );
}
